import { ALL_LOCATIONS } from '@/src/contexts/LocationsContext'
import { useLoadPlanningCurrentLocation } from '@/src/features/procurement-and-markets/load-planning/contexts/LoadPlanningCurrentLocationContext'
import {
  type LoadPlanningAdjustmentFormEmptyable,
  type LoadPlanningAdjustmentFormValidated,
  formValidationSchema,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/LoadPlanningAdjustment.types'
import {
  useCreateAdjustmentMutation,
  useUpdateAdjustmentMutation,
} from '@/src/features/procurement-and-markets/load-planning/load-planning-adjustments/hooks/AdjustmentMutation.hooks'
import { getLastYearsWeekRange } from '@/src/features/procurement-and-markets/load-planning/load-planning-graph/LoadPlanning.utils'
import { useBaseToast } from '@/src/hooks/useBaseToast'
import { endOfMonth, startOfMonth } from 'date-fns'
import { useFormik } from 'formik'
import { useTranslation } from 'react-i18next'
import { fromExclusiveEndDateIso, toExclusiveEndDateIso } from '../LoadPlanningAdjustment.utils'
import { mapAdjustmentStringToEnum, mapQuantityAdjustmentType } from '../loadPlanning.mapper'
import type { AdjustmentRow } from '../table/adjustmentTable.types'

export const useCreateLoadPlanningAdjustmentForm = (
  adjustment: AdjustmentRow | undefined,
  loadPlanningYear: number,
  onSuccess: () => void
) => {
  const { mutateAsync: createAdjustment } = useCreateAdjustmentMutation()
  const { mutateAsync: updateAdjustment } = useUpdateAdjustmentMutation()
  const toast = useBaseToast()
  const { t } = useTranslation()
  const { locationId: rawDefaultLocationId } = useLoadPlanningCurrentLocation()
  const defaultLocationId = rawDefaultLocationId !== ALL_LOCATIONS ? rawDefaultLocationId : undefined
  const isEditing = !!adjustment

  const data: LoadPlanningAdjustmentFormEmptyable = adjustment
    ? {
        ...adjustment,
        type: mapAdjustmentStringToEnum(adjustment.type),
        adjustmentType: mapQuantityAdjustmentType(adjustment.type),
        allDay: !adjustment.timeFrom && !adjustment.timeTo,
        timeframeEnd: fromExclusiveEndDateIso(adjustment.timeframeEnd) as string,
        referencePeriodEnd: fromExclusiveEndDateIso(adjustment.referencePeriodEnd),
      }
    : {
        applicableWeekdays: ['monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'],
        allDay: true,
      }

  const currentDate = new Date()
  const currentDateInTheTargetedYear = new Date(loadPlanningYear, currentDate.getMonth(), currentDate.getDate())
  const formik = useFormik<LoadPlanningAdjustmentFormEmptyable>({
    initialValues: {
      id: data.id,
      locationId: adjustment?.scope === 'COMPANY' ? ALL_LOCATIONS : data.locationId ?? defaultLocationId,
      name: data.name ?? '',
      type: data.type,
      timeframeStart: data.timeframeStart ?? startOfMonth(currentDateInTheTargetedYear).toISOString(),
      timeframeEnd: data.timeframeEnd ?? endOfMonth(currentDateInTheTargetedYear).toISOString(),
      comment: data.comment ?? '',
      // Company Holidays specific
      baseLoad: data.baseLoad,
      // Quantity Adjustments specific (both absolute and percentage)
      adjustmentType: data.adjustmentType ?? 'percentage',
      adjustmentValue: data.adjustmentValue,
      adjustmentValueUnit: data.adjustmentValueUnit ?? 'kW',
      applicableWeekdays: data.applicableWeekdays ? data.applicableWeekdays.map(weekday => weekday.toLowerCase()) : undefined,
      timeFrom: data.timeFrom,
      timeTo: data.timeTo,
      allDay: data.allDay,
      // Reference period specific
      referencePeriodStart: data.referencePeriodStart ?? getLastYearsWeekRange()[0].toISOString(),
      referencePeriodEnd: data.referencePeriodEnd ?? getLastYearsWeekRange()[1].toISOString(),
      percentageAdjustmentValue: data.percentageAdjustmentValue,
      // Renewable production specific
      // Saved values are always loaded in MWh, see toAnnualProductionMwh
      totalAnnualProduction: data.totalAnnualProduction,
      totalAnnualProductionUnit: 'MWh',
      sourceProfile: data.sourceProfile ?? 'SOLAR',
    },
    validationSchema: formValidationSchema,
    onSubmit: async (values: LoadPlanningAdjustmentFormEmptyable) => {
      // Casting because this has been validated internally by formik (transforming emptyable form -> validated form )
      const validated = values as LoadPlanningAdjustmentFormValidated
      const payload: LoadPlanningAdjustmentFormValidated = {
        ...validated,
        timeframeEnd: toExclusiveEndDateIso(validated.timeframeEnd) as string,
        referencePeriodEnd: toExclusiveEndDateIso(validated.referencePeriodEnd),
      }

      try {
        if (isEditing) {
          if (!payload.id) {
            toast({ status: 'error', title: t('components.procurement.forecasting.modal.form.error_notice') })
            return
          }

          await updateAdjustment({ ...payload, id: payload.id })
        } else {
          await createAdjustment(payload)
        }

        toast({ status: 'success', title: t('components.procurement.forecasting.modal.form.success_notice') })
        onSuccess()
      } catch {
        toast({ status: 'error', title: t('components.procurement.forecasting.modal.form.error_notice') })
      }
    },
  })

  return {
    formik,
    isEditing,
  }
}
